import { AlertCircle, ArrowRight, Coins, Info, Landmark, LifeBuoy, RefreshCw, Wallet } from "lucide-react";
import { type ReactNode, useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaEmpty } from "@/shared/components/feedback/ColocaEmpty";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaChoiceCards } from "@/shared/components/forms/ColocaChoiceCards";
import { ColocaReveal } from "@/shared/components/motion/ColocaReveal";
import { ColocaStepper } from "@/shared/components/navigation/ColocaStepper";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import useTranslations from "@/shared/lang/useTranslations";

import type { CardFundingOptions, CardFundingSourceKind } from "../../types/cardFundingOptions.types";
import type { CardDepositSubject } from "../../utils/cardDeposit";
import {
  defaultFundingSource,
  fundingUnavailableKey,
  hasUsableFundingSource,
  listedFundingSources,
  minutesFor,
} from "../../utils/cardFunding";
import { formatCardBalance } from "../../utils/walletMovementFormat";
import { CardDepositPanel } from "../CardDepositPanel";
import { CardLoadTimeline } from "./CardLoadTimeline";

type Step = "source" | "crypto";

const SOURCE_ICON: Record<CardFundingSourceKind, ReactNode> = {
  COP: <Landmark className="size-5" />,
  CRYPTO: <Coins className="size-5" />,
  CARD_WALLET: <Wallet className="size-5" />,
};

export interface CardLoadDialogProps {
  open: boolean;
  card: CardDepositSubject;
  last4: string;
  options: CardFundingOptions | null;
  isLoading: boolean;
  hasError: boolean;
  onRetry: () => void;
  /** The COP wallet balance, already formatted; null while unknown. */
  copBalance: string | null;
  /** Open straight on a source: the overview's "Recibir cripto" row. */
  initialSource?: CardFundingSourceKind | null;
  onClose: () => void;
  /** Pesos and the card wallet have their own dialogs; this one closes and hands over (no stacked dialogs). */
  onChooseCop: () => void;
  onChooseWallet: () => void;
}

/**
 * "Cargar": one place to choose how money reaches the card. Pesos and the card wallet continue in their own
 * dialogs; crypto is answered here, with the address and what happens after sending. Without the card wallet
 * a fixed notice says what loading means: the balance stays on the card and can only move to another card.
 */
export function CardLoadDialog({
  open,
  card,
  last4,
  options,
  isLoading,
  hasError,
  onRetry,
  copBalance,
  initialSource = null,
  onClose,
  onChooseCop,
  onChooseWallet,
}: CardLoadDialogProps) {
  const { t } = useTranslations("cards");
  const [step, setStep] = useState<Step>(initialSource === "CRYPTO" ? "crypto" : "source");
  const [picked, setPicked] = useState<CardFundingSourceKind | "">("");

  const sources = listedFundingSources(options);
  const usable = hasUsableFundingSource(options);
  // The recommended source until the user picks one; a reload of the options never overrides a pick.
  const choice = picked || initialSource || defaultFundingSource(options) || "";

  const selected = sources.find((source) => source.kind === choice);
  const canContinue = !!selected?.available;
  const noWithdrawals = !!options && !options.withdrawals_allowed;

  const describe = (kind: CardFundingSourceKind): string => {
    const source = sources.find((entry) => entry.kind === kind);
    if (source && !source.available) return t(fundingUnavailableKey(source));
    if (kind === "COP") {
      const eta = source?.estimated_seconds;
      const hint = eta
        ? t("card_load_source_cop_hint", { minutes: minutesFor(eta) })
        : t("card_load_source_cop_hint_plain");
      return copBalance ? `${hint} ${t("card_load_source_cop_balance", { amount: copBalance })}` : hint;
    }
    if (kind === "CRYPTO") return t("card_load_source_crypto_hint");
    // A balance that could not be read is not shown as $0.00.
    return options?.card_wallet_balance != null
      ? t("card_load_source_wallet_hint", { amount: formatCardBalance(Number(options.card_wallet_balance)) })
      : t("card_load_source_wallet_hint_plain");
  };

  const label = (kind: CardFundingSourceKind): string =>
    kind === "COP"
      ? t("card_load_source_cop")
      : kind === "CRYPTO"
        ? t("card_load_source_crypto")
        : t("card_load_source_wallet");

  const proceed = () => {
    if (!selected?.available) return;
    if (selected.kind === "COP") onChooseCop();
    else if (selected.kind === "CARD_WALLET") onChooseWallet();
    else setStep("crypto");
  };

  const footer =
    step === "crypto" ? (
      <>
        {initialSource !== "CRYPTO" && (
          <ColocaButton variant="ghost" onClick={() => setStep("source")}>
            {t("card_load_back")}
          </ColocaButton>
        )}
        <ColocaButton variant="primary" onClick={onClose}>
          {t("card_load_close")}
        </ColocaButton>
      </>
    ) : (
      <>
        <ColocaButton variant="ghost" onClick={onClose}>
          {t("card_load_cancel")}
        </ColocaButton>
        {usable && (
          <ColocaButton variant="primary" iconTrailing={ArrowRight} onClick={proceed} disabled={!canContinue}>
            {t("card_load_continue")}
          </ColocaButton>
        )}
      </>
    );

  return (
    <ColocaDialog
      open={open}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      size="default"
      mobileFullscreen
      title={t("card_load_title")}
      description={t("card_load_description", { last4 })}
      toolbar={
        usable && initialSource !== "CRYPTO" ? (
          <ColocaStepper
            aria-label={t("card_load_title")}
            currentStep={step === "source" ? 0 : 1}
            steps={[{ label: t("card_load_step_source") }, { label: t("card_load_step_details") }]}
          />
        ) : undefined
      }
      footer={footer}
    >
      {step === "crypto" ? (
        <ColocaReveal className="flex flex-col gap-4">
          <div data-reveal>
            <CardDepositPanel card={card} />
          </div>
          <section data-reveal className="flex flex-col gap-3 rounded-xl border border-border bg-card p-4">
            <h3 className="text-sm font-medium text-foreground">{t("card_load_crypto_next_title")}</h3>
            <CardLoadTimeline
              aria-label={t("card_load_crypto_next_title")}
              steps={[
                { id: "send", label: t("card_load_crypto_next_send"), state: "active" },
                { id: "confirm", label: t("card_load_crypto_next_confirm"), state: "pending" },
                { id: "credit", label: t("card_load_crypto_next_credit"), state: "pending" },
              ]}
            />
            <p className="text-xs text-muted-foreground">{t("card_load_crypto_eta")}</p>
          </section>
          {noWithdrawals && (
            <div data-reveal>
              <ColocaAlert variant="secondary" icon={Info} text={t("card_load_no_withdraw_notice")} />
            </div>
          )}
        </ColocaReveal>
      ) : isLoading ? (
        <div className="flex flex-col gap-3" aria-busy="true">
          <ColocaSkeleton className="h-6 w-48" />
          <ColocaSkeleton className="h-20 w-full" />
          <ColocaSkeleton className="h-20 w-full" />
        </div>
      ) : hasError ? (
        <ColocaAlert
          variant="destructive"
          icon={AlertCircle}
          text={t("card_load_error")}
          actions={
            <ColocaButton variant="default" size="sm" icon={RefreshCw} onClick={onRetry}>
              {t("card_load_retry")}
            </ColocaButton>
          }
        />
      ) : !usable ? (
        <ColocaEmpty
          icon={LifeBuoy}
          surface="card"
          title={t("card_load_empty_title")}
          description={t("card_load_empty_body")}
        />
      ) : (
        <ColocaReveal className="flex flex-col gap-4">
          <h3 data-reveal id="card-load-source-heading" className="text-sm font-medium text-foreground">
            {t("card_load_source_heading")}
          </h3>
          <div data-reveal>
            <ColocaChoiceCards
              aria-labelledby="card-load-source-heading"
              columns={1}
              value={choice}
              onValueChange={(value) => setPicked(value as CardFundingSourceKind)}
              items={sources.map((source) => ({
                value: source.kind,
                icon: SOURCE_ICON[source.kind],
                label: label(source.kind),
                description: describe(source.kind),
                disabled: !source.available,
              }))}
            />
          </div>
          {noWithdrawals && (
            <div data-reveal>
              <ColocaAlert variant="secondary" icon={Info} text={t("card_load_no_withdraw_notice")} />
            </div>
          )}
        </ColocaReveal>
      )}
    </ColocaDialog>
  );
}
